/**
 * Status Timeline Component
 * Displays a vertical timeline of booking status changes
 * Supports both legacy stages (completado, cancelado) and unified statuses (COMPLETED, CANCELLED)
 */


interface StatusHistoryEntry {
  id: string;
  fromStage: string | null;
  toStage: string;
  publicNote: string | null;
  createdAt: string;
  toStatusInfo?: {
    code: string;
    name: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
    statusType: string;
  } | null;
}

interface StatusTimelineProps {
  entries: StatusHistoryEntry[];
  language?: 'es' | 'en';
}

const FALLBACK_STATUS_COLOR = '#9CA3AF';

export function StatusTimeline({ entries, language = 'es' }: StatusTimelineProps) {
  const t = language === 'es' ? translations.es : translations.en;

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
        month: 'short',
        day: 'numeric',
      }),
      time: date.toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  };

  const getStageLabel = (stage: string, statusInfo?: { name?: string | null }): string => {
    return statusInfo?.name || stage;
  };

  const getStageColor = (statusInfo?: { color?: string | null }): string => {
    return statusInfo?.color || FALLBACK_STATUS_COLOR;
  };

  // Sort entries by date, most recent first
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  if (sortedEntries.length === 0) {
    return (
      <p className="text-gray-500 text-center py-4">{t.noHistory}</p>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {sortedEntries.map((entry, idx) => {
          const { date, time } = formatDateTime(entry.createdAt);
          const isLast = idx === sortedEntries.length - 1;

          return (
            <li key={entry.id}>
              <div className="relative pb-8">
                {/* Connector line */}
                {!isLast && (
                  <span
                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}

                <div className="relative flex space-x-3">
                  {/* Icon */}
                  <div>
                    <span
                      className="h-8 w-8 rounded-full flex items-center justify-center ring-4 ring-white text-white"
                      style={{ backgroundColor: getStageColor(entry.toStatusInfo ?? undefined) }}
                    >
                      <span className="text-xs">•</span>
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        {getStageLabel(entry.toStage, entry.toStatusInfo ?? undefined)}
                      </p>
                      <div className="text-right text-sm text-gray-500">
                        <p>{date}</p>
                        <p>{time}</p>
                      </div>
                    </div>

                    {entry.fromStage && (
                      <p className="mt-0.5 text-xs text-gray-400">
                        {t.from} {getStageLabel(entry.fromStage)}
                      </p>
                    )}

                    {entry.publicNote && (
                      <div className="mt-2 text-sm text-gray-600 bg-gray-50 rounded p-2">
                        {entry.publicNote}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const translations = {
  es: {
    noHistory: 'No hay historial de estados',
    from: 'Desde:',
  },
  en: {
    noHistory: 'No status history',
    from: 'From:',
  },
};

export default StatusTimeline;
